import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "./useAuth";
import { getLegalStatus } from "@/integrations/backend/legal";

/**
 * Redirects clients who still need to accept legal documents to the legal tab.
 * Use inside any client-facing page to ensure the legal modal remains blocking.
 */
export function useEnforceLegalApproval() {
  const { profile, isClient, loading, sessionChecked } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const checkedStatusRef = useRef(false);
  const loggedRef = useRef(false);

  useEffect(() => {
    if (loading || !sessionChecked) {
      if (!loggedRef.current) {
        console.log("[legal] enforce: waiting", { loading, sessionChecked });
        loggedRef.current = true;
      }
      return;
    }
    if (!isClient) return;

    if (profile?.legal_document_approval_needed) {
      console.log("[legal] enforce: profile flag true → redirect if not on legal", {
        path: location.pathname,
        search: location.search,
      });
      const isAlreadyOnLegal = location.pathname === "/client-profile" && location.search.includes("tab=legal");
      if (!isAlreadyOnLegal) {
        navigate("/client-profile?tab=legal", { replace: true });
      }
      return;
    }

    // Fallback: if backend flag is missing, fetch legal status once to enforce
    if (!checkedStatusRef.current) {
      checkedStatusRef.current = true;
      (async () => {
        try {
          const status = await getLegalStatus();
          const needsAcceptance = status.some((item) => !item.is_accepted);
          console.log("[legal] enforce: status fetched", {
            needsAcceptance,
            incompleteTypes: status.filter((i) => !i.is_accepted).map((i) => i.document.type),
            path: location.pathname,
            search: location.search,
          });
          if (needsAcceptance) {
            const isAlreadyOnLegal = location.pathname === "/client-profile" && location.search.includes("tab=legal");
            if (!isAlreadyOnLegal) {
              navigate("/client-profile?tab=legal", { replace: true });
            }
          }
        } catch (err) {
          // Best-effort; ignore errors to avoid blocking pages due to network issues
          console.warn("[legal] enforce: status check failed", err);
        }
      })();
    }
  }, [loading, sessionChecked, isClient, profile?.legal_document_approval_needed, location.pathname, location.search, navigate]);
}


